/**
 * SPEC-TrainingSimulator §8.1 — trainee home at /train.
 *
 * Five phase cards (0–4). Phase 0 is lessons only ("read n of N"). Each card
 * shows locked · available · in progress (n of N) · complete, with the
 * locked reason spelled out. No score anywhere on this surface (D7). Senior /
 * Admin see the same page in preview (§3): flagged in the header, nothing
 * recorded, Start disabled — they preview items from the key editor.
 */

import { Link, useLocation } from "wouter";
import { useToast } from "@/hooks/use-toast";
import { Starling } from "@/lib/starling-brand";
import { useRunnerHome, useStartAttempt } from "@/lib/simulator-runner-queries";
import type { PhaseCard } from "@shared/simulator-helpers";

const T = {
  bg: "#F7F8FA",
  card: "#FFFFFF",
  border: "#E5E7EB",
  text: "#111827",
  txt2: "#6B7280",
  txt3: "#9CA3AF",
  sub: "#F3F4F6",
  purple: Starling.purple,
  purpleSub: "rgba(89,47,150,.08)",
  good: "#166534",
  goodBg: "#DCFCE7",
  amber: "#B45309",
  amberBg: "#FEF3C7",
} as const;

// Item counts come from the served card (`phaseSizes` is Admin-configurable, §5.1).
function phaseBlurb(card: PhaseCard): string {
  const n = card.progress.total;
  const items = `${n} deliver${n === 1 ? "y" : "ies"}`;
  switch (card.phase) {
    case 0: return "Read the orientation lessons before you start.";
    case 1: return `${items}, one question at a time, with instant feedback. No clock.`;
    case 2: return `${items}, questions answered silently, review at the end. Soft clock.`;
    case 3: return `${items} with the real footer and an extendable clock.`;
    default: return `${items} on a hard clock. Your Senior signs off on this one.`;
  }
}

function stateChip(card: PhaseCard) {
  const base = { padding: "3px 9px", borderRadius: 999, fontSize: 11.5, fontWeight: 700 } as const;
  switch (card.state) {
    case "complete": return <span data-testid={`phase-state-${card.phase}`} style={{ ...base, background: T.goodBg, color: T.good }}>Complete ✓</span>;
    case "in_progress": return <span data-testid={`phase-state-${card.phase}`} style={{ ...base, background: T.amberBg, color: T.amber }}>In progress · {card.progress.done} of {card.progress.total}</span>;
    case "available": return <span data-testid={`phase-state-${card.phase}`} style={{ ...base, background: T.purpleSub, color: T.purple }}>Available</span>;
    default: return <span data-testid={`phase-state-${card.phase}`} style={{ ...base, background: T.sub, color: T.txt3 }}>Locked</span>;
  }
}

export default function TrainHome() {
  const home = useRunnerHome();
  const start = useStartAttempt();
  const [, navigate] = useLocation();
  const { toast } = useToast();

  if (home.isLoading) return <div style={{ padding: 24, fontFamily: Starling.fontBody }}>Loading training…</div>;
  if (home.isError || !home.data) return <div style={{ padding: 24, fontFamily: Starling.fontBody, color: "#B91C1C" }}>Couldn't load your training home. Ask your Senior.</div>;

  const { preview, trainee, phases, orientation } = home.data;
  const firstUnread = orientation.lessons.find((l) => !l.read) ?? orientation.lessons[0] ?? null;

  const onStart = async (card: PhaseCard) => {
    if (card.state === "in_progress" && card.attemptId) { navigate(`/train/run/${card.attemptId}`); return; }
    try {
      const r = await start.mutateAsync(card.phase);
      navigate(`/train/run/${r.attempt.id}`);
    } catch (e: any) {
      const msg = String(e?.message ?? e).replace(/^\d{3}:\s*/, "");
      let description = msg;
      try { description = JSON.parse(msg).message ?? msg; } catch { /* raw */ }
      toast({ title: "Can't start this phase", description, variant: "destructive" });
      home.refetch();
    }
  };

  return (
    <div data-testid="train-home" style={{ padding: "18px 22px 40px", fontFamily: Starling.fontBody, color: T.text, maxWidth: 900 }}>
      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 12, flexWrap: "wrap", marginBottom: 6 }}>
        <div style={{ fontFamily: Starling.fontHead, fontSize: 22, fontWeight: 600 }}>Training</div>
        {preview && (
          <span data-testid="train-preview-flag" style={{ padding: "3px 10px", borderRadius: 999, fontSize: 11.5, fontWeight: 700, background: T.amberBg, color: T.amber }}>
            Preview · nothing is recorded
          </span>
        )}
      </div>
      <div style={{ fontSize: 13.5, color: T.txt2, marginBottom: 16 }}>
        {preview
          ? <>You are seeing what a trainee sees. To try an item, open it from the <Link href="/train/manage" style={{ color: T.purple }}>Training set</Link> key editor → <b>Preview as trainee</b>.</>
          : <>Welcome, {trainee.name}. Work through the phases in order — each one unlocks the next.</>}
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {phases.map((card) => {
          const locked = card.state === "locked";
          return (
            <div
              key={card.phase}
              data-testid={`phase-card-${card.phase}`}
              data-state={card.state}
              style={{
                background: T.card, border: `1px solid ${card.state === "available" || card.state === "in_progress" ? T.purple : T.border}`, borderRadius: 10, padding: "14px 16px",
                display: "grid", gridTemplateColumns: "1fr auto", gap: 14, alignItems: "center", opacity: locked ? 0.75 : 1,
              }}
            >
              <div style={{ minWidth: 0 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                  <div style={{ fontFamily: Starling.fontHead, fontSize: 16, fontWeight: 600 }}>Phase {card.phase} · {card.label}</div>
                  {stateChip(card)}
                </div>
                <div style={{ fontSize: 12.5, color: T.txt2, marginTop: 4 }}>{phaseBlurb(card)}</div>
                {card.phase === 0 && (
                  <div data-testid="phase-0-progress" style={{ fontSize: 12.5, color: T.txt2, marginTop: 4 }}>
                    Read {card.progress.done} of {card.progress.total} lesson{card.progress.total === 1 ? "" : "s"}
                  </div>
                )}
                {locked && card.lockedReason && (
                  <div data-testid={`phase-locked-${card.phase}`} style={{ fontSize: 12.5, color: T.amber, marginTop: 4, fontWeight: 600 }}>{card.lockedReason}</div>
                )}
              </div>
              <div>
                {card.phase === 0 ? (
                  firstUnread ? (
                    <Link href={`/train/lesson/${firstUnread.id}`} data-testid="phase-start-0" style={btn(card.state !== "complete")}>
                      {card.state === "complete" ? "Read again" : card.state === "in_progress" ? "Continue reading" : "Read orientation"}
                    </Link>
                  ) : (
                    <span style={{ fontSize: 12, color: T.txt3 }}>No lessons yet</span>
                  )
                ) : card.state === "available" || card.state === "in_progress" ? (
                  <button
                    type="button"
                    data-testid={`phase-start-${card.phase}`}
                    disabled={preview || start.isPending}
                    title={preview ? "Preview mode — open an item from the key editor" : undefined}
                    onClick={() => onStart(card)}
                    style={{ ...btn(true), opacity: preview ? 0.5 : 1, cursor: preview ? "not-allowed" : "pointer" }}
                  >
                    {card.state === "in_progress" ? "Resume" : "Start"}
                  </button>
                ) : card.state === "complete" ? (
                  <span style={{ fontSize: 12.5, color: T.good, fontWeight: 700 }}>Done</span>
                ) : (
                  <span style={{ fontSize: 12.5, color: T.txt3 }}>—</span>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function btn(primary: boolean): React.CSSProperties {
  return {
    display: "inline-block", padding: "8px 18px", borderRadius: 8, fontSize: 13, fontWeight: 700, fontFamily: Starling.fontBody, textDecoration: "none", cursor: "pointer",
    border: `1px solid ${primary ? T.purple : T.border}`, background: primary ? T.purple : T.card, color: primary ? "#fff" : T.txt2,
  };
}
